import React from "react";
import WatermarkedMedia from "./WatermarkedMedia";
import Editable from "./Editable";
import { ChevronDown } from "lucide-react";

export const Hero = ({ data = {}, editable, onChange, onMediaClick }) => {
  const setField = (k, v) => onChange && onChange({ ...data, [k]: v });

  const titleLines = data.title_lines || ["Crafting Cinema.", "Curating Stars."];

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section className="relative h-screen min-h-[680px] w-full overflow-hidden bg-onyx-900" data-testid="hero-section">
      {/* Background media with watermark */}
      <div className={`absolute inset-0 ${editable ? "" : "slow-zoom"}`}>
        <WatermarkedMedia
          src={data.background_url}
          videoSrc={data.video_url}
          alt="Mumbai Film City production"
          position="top-right"
          opacity={0.6}
          watermarkSize={64}
          className="h-full w-full"
          testId="hero-background-media"
        />
      </div>

      {/* Cinematic vignette + grain */}
      <div className="absolute inset-0 vignette grain pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-b from-onyx-900/40 via-transparent to-onyx-900/85 pointer-events-none" />

      {/* Edit cover button */}
      {editable && (
        <button
          onClick={onMediaClick}
          className="absolute top-24 right-6 z-30 font-cinzel text-[10px] tracking-[0.3em] uppercase text-onyx-900 bg-gold hover:bg-gold-bright px-4 py-2 transition-colors"
          data-testid="hero-edit-media-btn"
        >
          Replace Background
        </button>
      )}

      {/* Content */}
      <div className="relative z-10 flex h-full items-center">
        <div className="mx-auto w-full max-w-[1400px] px-6 md:px-10">
          <div className="max-w-3xl fade-up">
            <div className="flex items-center gap-3 mb-7">
              <div className="h-px w-10 bg-gold" />
              <Editable
                editable={editable}
                value={data.eyebrow}
                onChange={(v) => setField("eyebrow", v)}
                className="font-cinzel text-[11px] tracking-[0.4em] uppercase text-gold"
                testId="hero-eyebrow"
              />
            </div>

            <h1 className="font-display text-5xl leading-[1.02] sm:text-6xl lg:text-[5.5rem]">
              {titleLines.map((line, i) => (
                <span key={`title-${i}-${(line || "").slice(0, 8)}`} className="block">
                  <Editable
                    as="span"
                    editable={editable}
                    value={line}
                    onChange={(v) => {
                      const nl = [...titleLines];
                      nl[i] = v;
                      setField("title_lines", nl);
                    }}
                    className={i === 1 ? "italic text-gold-gradient" : "text-ivory"}
                    testId={`hero-title-line-${i}`}
                  />
                </span>
              ))}
            </h1>

            <Editable
              as="p"
              multiline
              editable={editable}
              value={data.subtitle}
              onChange={(v) => setField("subtitle", v)}
              className="mt-8 max-w-xl text-ivory/75 text-base md:text-lg leading-relaxed"
              testId="hero-subtitle"
            />

            <div className="mt-12 flex flex-wrap items-center gap-5">
              <button
                onClick={() => scrollTo("portfolio")}
                className="group relative overflow-hidden bg-gold px-8 py-4 font-cinzel text-[11px] tracking-[0.3em] uppercase text-onyx-900 hover:bg-gold-bright transition-colors"
                data-testid="hero-cta-primary"
              >
                <span className="relative z-10">{data.cta_primary || "View Our Work"}</span>
              </button>
              <button
                onClick={() => scrollTo("contact")}
                className="font-cinzel text-[11px] tracking-[0.3em] uppercase border border-gold/40 hover:border-gold text-ivory hover:text-gold px-8 py-4 transition-all"
                data-testid="hero-cta-secondary"
              >
                {data.cta_secondary || "Get In Touch"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-gold/70 animate-bounce">
        <span className="font-cinzel text-[9px] tracking-[0.4em] uppercase">Scroll</span>
        <ChevronDown size={16} />
      </div>
    </section>
  );
};

export default Hero;
